<script lang="ts">
	import { mutation, query } from "@api/graphql.js";
	import sendApplication from "@api/mutations/sendApplication.graphql";
	import getDogByChipNumber from "@api/queries/getDogByChipNumber.graphql";
	import bgBotShape from "@assets/images/shape-bottom.svg";
	import bgTopShape from "@assets/images/shape-top.svg";
	import ProductCard from "@components/ProductCard.svelte";
	import { applicationFormId } from "@components/applicationFormId.js";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Media from "@components/common/Media.svelte";
	import Modal from "@components/common/Modal.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import ErrorMessages from "@components/form/ErrorMessages.svelte";
	import FileUploader from "@components/form/FileUploader.svelte";
	import Form from "@components/form/Form.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import LabelledSelect from "@components/form/LabelledSelect.svelte";
	import { getFormFields } from "@utils/getFormFields.js";
	import { getTranslate } from "@utils/translate.js";
	import addIcon from "@core/assets/icons/add.svg?raw";
	import audioUpload from "@core/assets/icons/audio-upload.svg?raw";
	import removeIcon from "@core/assets/icons/cross.svg?raw";
	import type { Application, CreateApplicationInput } from "@core/schema/Application.js";
	import type { ApplicationValidationErrors } from "@core/schema/ApplicationCustomInvocation.js";
	import { Breed } from "@core/schema/Breed.js";
	import type { Competition } from "@core/schema/Competition.js";
	import { CompetitionCategoryType } from "@core/schema/CompetitionCategoryType.js";
	import { Country } from "@core/schema/Country.js";
	import type { Dog } from "@core/schema/Dog.js";
	import type { Gender } from "@core/schema/Gender.js";
	import type { Locale } from "@core/schema/Locale.js";
	import type { TShirtSize } from "@core/schema/TShirtSize.js";
	import { Team } from "@core/schema/Team.js";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { competitionCategoryTypeIncompatibilities } from "@core/schema/competitionCategoryTypeIncompatibilities.js";
	import { competitionCategoryTypesWithRequiredAudio } from "@core/schema/competitionCategoryTypesWithRequiredAudio.js";
	import { competitionCategoryTypesWithTshirt } from "@core/schema/competitionCategoryTypesWithTshirt.js";
	import { breedNameMap } from "@core/schema/names/breedNameMap.js";
	import { competitionCategoryTypeNameMap } from "@core/schema/names/competitionCategoryTypeNameMap.js";
	import { countryNameMap } from "@core/schema/names/countryNameMap.js";
	import { teamNameMap } from "@core/schema/names/teamNameMap.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getMaximumBirthdateOfDog } from "@core/schema/utils/getMaximumBirthdateOfDog.js";
	import { createApplicationCreateInputSchema } from "@core/schema/zod/applicationSchema.js";
	import type { DeepComplete } from "@core/utils/DeepComplete.js";
	import type { DeepPartial } from "@core/utils/DeepPartial.js";
	import { AppSyncError } from "@core/utils/aws/appsync/AppSyncError.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { isZodError } from "@core/utils/isZodError.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./ApplicationForm.translations.json" with { type: "json" };
	import Icon from "./common/Icon.svelte";

	const { competition }: { competition: Competition } = $props();

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();
	const applicationInputSchema = createApplicationCreateInputSchema(tenant.locale);
	const translate = getTranslate(translations);

	const send = createPromiseWithStatus((input: CreateApplicationInput) =>
		mutation<Application, { input: CreateApplicationInput; locale: Locale }>(PUBLIC_GRAPHQL_ENDPOINT, sendApplication, {
			input,
			locale: tenant.locale,
		}),
	);
	const getDog = createPromiseWithStatus((chipNumber: Dog["chipNumber"]) =>
		query<Dog | undefined, { chipNumber: Dog["chipNumber"] }>(PUBLIC_GRAPHQL_ENDPOINT, getDogByChipNumber, {
			chipNumber,
		}),
	);

	type Input = Omit<DeepComplete<DeepPartial<CreateApplicationInput>>, "audio"> & {
		audio: CreateApplicationInput["audio"];
	};

	let dogs = $state<{ id: ReturnType<typeof crypto.randomUUID>; done: boolean }[]>([
		{ id: crypto.randomUUID(), done: false },
	]);

	let uploadedAudio = $state<Record<(typeof dogs)[number]["id"], UploadedFile | undefined>>({});
	let formInputs = $state<[(typeof dogs)[number]["id"], Input][]>([]);
	let errors = $state<[(typeof dogs)[number]["id"], { error: unknown; input?: CreateApplicationInput }][]>([]);
	let successfulApplications = $state<[CreateApplicationInput, Application][]>([]);

	function addDog(): void {
		dogs.push({ id: crypto.randomUUID(), done: false });
	}
	function removeDog(toRemove: string): void {
		dogs = dogs.filter(({ id }) => id !== toRemove);
	}

	let recordingApprovedAt = $state<string | undefined>(undefined);
	let gdprApprovedAt = $state<string | undefined>(undefined);
	function onRecordingToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		recordingApprovedAt = input.checked ? new Date().toISOString() : undefined;
	}
	function onGdprToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		gdprApprovedAt = input.checked ? new Date().toISOString() : undefined;
	}

	function getFormInputs(fields: Record<string, string | undefined>): typeof formInputs {
		return dogs.map(({ id }) => {
			const competitionCategoryTypes: CompetitionCategoryType[] = [];
			for (const type of Object.values(CompetitionCategoryType)) {
				const group = competitionCategoryTypeIncompatibilities.find((group) => group.includes(type));
				if (group) {
					const key = group.join(":");
					const value = fields[`${id}-competitionCategoryTypes:${key}`];
					if (value === type) {
						competitionCategoryTypes.push(type);
					}
				} else if (fields[`${id}-competitionCategoryTypes:${type}`] === "on") {
					competitionCategoryTypes.push(type);
				}
			}
			return [
				id,
				{
					audio: competitionCategoryTypesWithRequiredAudio.some((type) => competitionCategoryTypes.includes(type))
						? uploadedAudio[id]
						: undefined,
					competitionCategoryTypes,
					competitionId: competition.id,
					dog: {
						birthdate: fields[`${id}-birthdate`],
						breed: fields[`${id}-customBreed`] ?? fields[`${id}-breed`],
						callName: fields[`${id}-callName`],
						chipNumber: fields[`${id}-chipNumber`],
						registeredName: fields[`${id}-registeredName`],
						handlerOwner: undefined,
					},
					gdprApprovedAt,
					handler: {
						email: fields.email,
						firstName: fields.firstName,
						homeland: fields.homeland as Country | undefined,
						lastName: fields.lastName,
						member: fields.member === "on",
					},
					isHandlerOwnerOfDog: fields[`${id}-isHandlerOwnerOfDog`] === "on",
					recordingApprovedAt,
					tShirt:
						fields.tShirtGender !== undefined && fields.tShirtSize !== undefined
							? {
									size: fields.tShirtSize as TShirtSize,
									gender: fields.tShirtGender as Gender,
								}
							: undefined,
					team: fields.team as Team | undefined,
				},
			];
		});
	}

	function onchange(event: Event): void {
		const input = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const fields = getFormFields(input.form!);
		formInputs = getFormInputs(fields);
		errors = []; // TODO (not that important): Only clear fields which changed.
	}

	const loadedDogs: Record<Dog["chipNumber"], Dog | null> = $state({});
	$effect(() => {
		if (getDog.value.status === "loading") {
			return;
		}

		for (const [_, input] of formInputs) {
			const chipNumber = input.dog?.chipNumber;
			if (!chipNumber || chipNumber in loadedDogs) {
				continue;
			}
			void getDog.call(chipNumber).then((dog) => {
				loadedDogs[chipNumber] = dog ?? null;
				input.dog = {
					chipNumber,
					breed: dog?.breed,
					callName: dog?.callName,
					registeredName: dog?.registeredName,
					birthdate: dog?.birthdate,
					handlerOwner: undefined,
				};
			});
			break;
		}
	});

	async function onsubmit(event: SubmitEvent): Promise<void> {
		errors = [];
		successfulApplications = [];
		event.preventDefault();
		const form = event.target as HTMLFormElement;
		const fields = getFormFields(form);
		const newErrors: typeof errors = [];

		for (const [dogId, formInput] of getFormInputs(fields)) {
			const dog = dogs.find(({ id }) => id === dogId);
			if (dog?.done) {
				continue;
			}

			const parsedInput = applicationInputSchema.safeParse(formInput);
			if (!parsedInput.success) {
				// eslint-disable-next-line no-console
				console.error(parsedInput.error);
				newErrors.push([dogId, { error: parsedInput.error }]);
				continue;
			}

			const { data: input } = parsedInput;
			try {
				const application = await send.call(input);
				successfulApplications.push([input, application]);
				dogs = dogs.map((dog) => (dog.id === dogId ? { ...dog, done: true } : dog));
				if (loadedDogs[input.dog.chipNumber] === null) {
					// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
					delete loadedDogs[input.dog.chipNumber];
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error);
				newErrors.push([dogId, { error, input }]);
			}
		}

		formInputs = getFormInputs(getFormFields(form));

		errors = newErrors;
	}

	function getValidationErrorMessages(input: CreateApplicationInput, error: unknown): string[] {
		if (!(error instanceof AppSyncError)) {
			return [translate("internalError")];
		}
		const [firstError] = error.errors;
		if (!firstError?.errorType) {
			return [translate("internalError")];
		}

		switch (firstError.errorType) {
			case "Application:Invalid": {
				const errors: string[] = [];
				const validationError = firstError.errorInfo as ApplicationValidationErrors;
				// eslint-disable-next-line no-console
				console.error(validationError);

				for (const category of validationError.missingAudioForCategories ?? []) {
					errors.push(translate("missingAudio", { category: competitionCategoryTypeNameMap[category][tenant.locale] }));
				}
				for (const category of validationError.categoriesOverLimit ?? []) {
					errors.push(translate("overLimit", { category: competitionCategoryTypeNameMap[category][tenant.locale] }));
				}
				for (const category of validationError.handlerDogAlreadyHaveApplicationInCategories ?? []) {
					errors.push(
						translate("alreadyExistingApplication", {
							category: competitionCategoryTypeNameMap[category][tenant.locale],
							dog: input.dog.callName,
						}),
					);
				}
				for (const categories of validationError.handlerOnlyAllowedOnceInCategories ?? []) {
					const [category, ...otherCategories] = categories;
					if (category !== undefined) {
						if (otherCategories.length > 0) {
							errors.push(
								translate("alreadyOnceInCategoryGroup", {
									categories: categories
										.map((category) => competitionCategoryTypeNameMap[category][tenant.locale])
										.join("/"),
								}),
							);
						} else {
							errors.push(
								translate("alreadyOnceInCategory", {
									category: competitionCategoryTypeNameMap[category][tenant.locale],
								}),
							);
						}
					}
				}
				for (const category of validationError.competitionDoesNotHaveCategories ?? []) {
					errors.push(
						translate("competitionNonexistentCategory", {
							category: competitionCategoryTypeNameMap[category][tenant.locale],
						}),
					);
				}
				for (const categories of validationError.categoryIncompatibilities ?? []) {
					errors.push(
						translate("categoryIncompatibilities", {
							categories: categories
								.map((category) => competitionCategoryTypeNameMap[category][tenant.locale])
								.join(", "),
						}),
					);
				}
				if (validationError.dogMaximumBirthdate) {
					errors.push(
						translate("maximumDogBirthdate", {
							date: toDateMonthYearFormat(validationError.dogMaximumBirthdate, tenant.locale),
						}),
					);
				}
				return errors;
			}
			default:
				return [translate("internalError")];
		}
	}

	const isBreed = isEnum(Breed);

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet failedApplication([_, { error, input }]: (typeof errors)[number])}
	<div class="flex flex-col gap-2 mt-4 text-primary">
		{#if input}
			{@const messages = getValidationErrorMessages(input, error)}
			<p class="font-bold">Problémy s přihláškou:</p>
			<ErrorMessages {messages} />
		{:else}
			<ErrorMessages messages={[translate("wronglyFilled")]} />
		{/if}
	</div>
{/snippet}

<Modal show={successfulApplications.length > 0}>
	<p class="text-md mb-4">{translate("thanksForApplication")}</p>
	<div class="items-left flex flex-col gap-4">
		<p class="font-bold">{translate("successfulApplications")}</p>
		<ul class="list-inside list-disc">
			{#each successfulApplications as [input, application] (application.id)}
				<li>
					{input.dog.chipNumber} | {input.dog.callName}: {application.categories
						.map(({ type }) => competitionCategoryTypeNameMap[type][tenant.locale])
						.join(", ")}
				</li>
			{/each}
		</ul>

		{#if errors.length > 0}
			<div class="flex flex-col">
				<p class="font-bold">{translate("errorApplications")}</p>
				{#each errors as error (error[0])}
					{@render failedApplication(error)}
				{/each}
			</div>
		{/if}

		<Button
			variant={ButtonVariant.Primary}
			class="ml-auto"
			onclick={(): void => {
				successfulApplications = [];
			}}
		>
			{translate("understand")}
		</Button>
	</div>
</Modal>

<Form class="full-width grid-container mt-16" id={applicationFormId[tenant.locale]} {onchange} {onsubmit}>
	<div class="bg-secondary full-width grid-container py-16 relative mb-16">
		<Media
			class="absolute -top-32 right-0 left-0 w-full h-36 object-cover z-10"
			source={{ src: bgTopShape, type: "image/svg+xml" }}
			width={1920}
			height={1080}
			alt="Background shape"
		></Media>

		<h2>{translate("application")}</h2>

		<div class="flex flex-col md:flex-row md:gap-16 lg:gap-32">
			<div class="pb-16 lg:py-4 w-72 max-w-full md:w-96 flex flex-col gap-4 lg:gap-6">
				<h3>{translate("handler")}</h3>
				<LabelledInput type="text" name="firstName" placeholder="John" required>
					{translate("handlerFirstname")}
				</LabelledInput>
				<LabelledInput type="text" name="lastName" placeholder="Doe" required>
					{translate("handlerLastname")}
				</LabelledInput>
				<LabelledInput type="email" name="email" placeholder="johndoe@gmail.com" required>
					{translate("email")}
				</LabelledInput>
				<LabelledSelect name="homeland" required>
					{translate("homeland")}
					{#snippet options()}
						<option value="">{translate("choose")}</option>
						{#each Object.values(Country) as country (country)}
							<option value={country}>{countryNameMap[country][tenant.locale]}</option>
						{/each}
					{/snippet}
				</LabelledSelect>
				<LabelledSelect name="team">
					{translate("team")}
					{#snippet options()}
						<option value="">{translate("none")}</option>
						{#each Object.values(Team) as team (team)}
							<option value={team}>{teamNameMap[team][tenant.locale]}</option>
						{/each}
					{/snippet}
				</LabelledSelect>

				<LabelledInput type="checkbox" name="member">
					{translate("declarationOfMembershipDiscDogClub")}
				</LabelledInput>
			</div>
			<Media
				class="grow mb-8 hidden md:block"
				source={competition.coverImage}
				width={828}
				height={554}
				alt={competition.headline[tenant.locale]}
			/>
		</div>

		<div class="py-4 lg:pt-32 flex flex-col gap-16 lg:gap-32">
			{#each dogs as dog, index (dog.id)}
				{@const [, formInput] = formInputs.find(([id]) => id === dog.id) ?? []}
				{@const error = errors.find(([id]) => id === dog.id)}
				{@const [, output] = error ?? []}
				{@const loadedDog = loadedDogs[formInput?.dog?.chipNumber ?? ""]}
				{@const loadingDog =
					getDog.value.status === "loading" && getDog.value.variables[0] === (formInput?.dog?.chipNumber ?? "")}
				{@const selectedCategoriesWhichRequireAudio = competitionCategoryTypesWithRequiredAudio.filter((type) =>
					formInput?.competitionCategoryTypes?.includes(type),
				)}
				<div class="grid md:grid-cols-2 gap-8 lg:gap-32 xl:gap-52">
					<div class="w-72 max-w-full md:w-96">
						<div class="flex justify-between items-center mb-8">
							<h3 class="mb-0" class:text-green-600={dog.done}>
								{translate("dog")} #{index + 1}
								{#if dog.done}
									- zaregistrováno
								{/if}
							</h3>
							{#if index > 0 && !dog.done}
								<Button
									class="min-w-fit h-fit flex items-center gap-2"
									variant={ButtonVariant.Primary}
									onclick={(): void => {
										removeDog(dog.id);
									}}
								>
									{translate("removeDog")}
									<Icon class="h-3 aspect-square" icon={removeIcon} />
								</Button>
							{/if}
						</div>

						<div class="flex flex-col gap-4 lg:gap-6">
							<LabelledInput
								type="text"
								name="{dog.id}-chipNumber"
								placeholder="933007125056789"
								required
								readonly={dog.done}
							>
								{translate("dogChipOrTatoo")}
							</LabelledInput>
							{#if loadingDog}
								<p>{translate("searchingDog")}</p>
							{:else if loadedDog !== undefined}
								<LabelledInput
									type="text"
									name="{dog.id}-registeredName"
									placeholder="Pretty II. Tiann"
									required
									value={loadedDog?.registeredName}
									readonly={loadedDog !== null || dog.done}
								>
									{translate("dogRegisteredName")}
								</LabelledInput>
								<LabelledInput
									type="text"
									name="{dog.id}-callName"
									placeholder="Leia"
									required
									value={loadedDog?.callName}
									readonly={loadedDog !== null || dog.done}
								>
									{translate("dogCallName")}
								</LabelledInput>
								<LabelledInput
									type="date"
									name="{dog.id}-birthdate"
									placeholder="05/08/2022"
									max={getMaximumBirthdateOfDog(
										competition,
										formInput?.competitionCategoryTypes?.filter((type) => type !== undefined) ?? [],
									).toISODate()}
									required
									value={loadedDog?.birthdate}
									readonly={loadedDog !== null || dog.done}
								>
									{translate("dogBirthdate")}
								</LabelledInput>
								{#if !loadedDog?.handlerOwner}
									<LabelledInput type="checkbox" name="{dog.id}-isHandlerOwnerOfDog" readonly={dog.done}>
										{translate("isDogOwner")}
									</LabelledInput>
								{/if}
								<LabelledSelect name="{dog.id}-breed" required>
									{translate("dogBreed")}
									{#snippet options()}
										<option value="" disabled={loadedDog !== null || dog.done}>{translate("choose")}</option>
										{#each Object.entries(breedNameMap) as [value, name] (value)}
											{@const selected = formInput?.dog?.breed === value || value === loadedDog?.breed}
											<option {value} {selected} disabled={!selected && (loadedDog !== null || dog.done)}>
												{name[tenant.locale]}
											</option>
										{/each}
										<option
											value="other"
											disabled={loadedDog !== null || dog.done}
											selected={loadedDog && !isBreed(loadedDog.breed)}
										>
											{translate("customBreed")}
										</option>
									{/snippet}
								</LabelledSelect>
								{#if (loadedDog ?? formInput?.dog)?.breed && !isBreed((loadedDog ?? formInput?.dog)?.breed)}
									<LabelledInput
										type="text"
										name="{dog.id}-customBreed"
										required
										value={loadedDog?.breed}
										readonly={loadedDog !== null || dog.done}
									>
										{translate("customDogBreed")}
									</LabelledInput>
								{/if}
							{/if}
						</div>
					</div>
					<div>
						<h3>{translate("disciplines")}</h3>
						<div class="flex flex-col gap-2">
							{#each Object.values(CompetitionCategoryType).filter( (categoryType) => competition.categories.some(({ type }) => type === categoryType), ) as category (category)}
								{@const incompatibilityGroup = competitionCategoryTypeIncompatibilities.find((group) =>
									group.includes(category),
								)}
								{@const disabled = dog.done}
								{@const name = `${dog.id}-competitionCategoryTypes:${incompatibilityGroup?.join(":") ?? category}`}
								<LabelledInput
									type={incompatibilityGroup ? "radio" : "checkbox"}
									{name}
									value={incompatibilityGroup ? category : undefined}
									{disabled}
									onclick={(event) => {
										// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
										const input = event.target as HTMLInputElement;
										if (input.type === "radio" && input.checked) {
											if (input.dataset.wasChecked === "true") {
												input.checked = false;
												input.dispatchEvent(new Event("input", { bubbles: true }));
												input.dispatchEvent(new Event("change", { bubbles: true }));
												delete input.dataset.wasChecked;
											} else {
												for (const input of document.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`)) {
													delete input.dataset.wasChecked;
												}
												input.dataset.wasChecked = "true";
											}
										}
									}}
								>
									{competitionCategoryTypeNameMap[category][tenant.locale]}
								</LabelledInput>
							{/each}
						</div>
						{#if selectedCategoriesWhichRequireAudio.length > 0}
							<div class="mt-4 lg:mt-6 max-w-96">
								<!-- Required in FileUploader does not work correctly, we have schema validation anyways, so I can't care much anymore. -->
								<FileUploader
									destinationFolder="audio/{dog.id}"
									accept={["audio/mp3", "audio/mpeg", "audio/wav"]}
									uploadIcon={audioUpload}
									onchange={(file: UploadedFile | undefined): void => {
										uploadedAudio[dog.id] = file;
										uploadedAudio = uploadedAudio;
									}}
									disabled={dog.done}
								/>
							</div>
						{/if}

						{#if isZodError<CreateApplicationInput>(output?.error) && output.error.formErrors.fieldErrors.competitionCategoryTypes}
							<ErrorMessages class="mt-4" messages={output.error.formErrors.fieldErrors.competitionCategoryTypes} />
						{/if}
					</div>
				</div>
				{#if error}
					{@render failedApplication(error)}
				{/if}
			{/each}
		</div>
		<div class="py-4">
			<Button class="max-md:w-full max-w-96 flex items-center gap-1" variant={ButtonVariant.Primary} onclick={addDog}>
				{translate("addDog")}
				<Icon class="h-5 aspect-square" icon={addIcon} />
			</Button>
		</div>
		<Media
			class="absolute -bottom-28 right-0 left-0 w-full h-40 object-cover z-10"
			source={{ src: bgBotShape, type: "image/svg+xml" }}
			width={1920}
			height={1080}
			alt="Background shape"
		></Media>
	</div>

	<div class="py-16 container-8">
		{#if formInputs.some( ([, input]) => input.competitionCategoryTypes?.some((type) => type !== undefined && competitionCategoryTypesWithTshirt.includes(type)), )}
			<h3 class="text-center md:text-left">{translate("selectTshirtSize")}</h3>
			<div class="flex flex-wrap justify-center md:justify-start">
				<ProductCard />
			</div>
		{/if}
	</div>

	<div class="py-4 lg:pb-16 max-w-xl container-8">
		<div class="flex flex-col gap-8">
			<LabelledInput type="checkbox" onchange={onRecordingToggle} required>
				{translate("consentPhotos")}
			</LabelledInput>
			<LabelledInput type="checkbox" onchange={onGdprToggle} required>
				<Formatted text={translate("consentGdpr", { path: getGdprPath(tenant.locale) })} />
			</LabelledInput>
		</div>
	</div>

	<div class="container-8">
		<Button
			type="submit"
			variant={ButtonVariant.Primary}
			disabled={send.value.status === "loading"}
			onclick={() => {
				errors = [];
			}}
		>
			{#if send.value.status === "loading"}
				{translate("sendingApplication")}
			{:else}
				{translate("sendApplication")}
			{/if}
		</Button>
	</div>
</Form>
